import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../Core/store';
import { axiosPrivate, axiosPublic } from '../Core/axios';
import { stripeStatus } from '../Helpers/types';
import { StripePlan } from '../Models/Requests/StripePlan';
import { StripePayment } from '../Models/Requests/StripePayment';

export interface StripeState {
  plans: Array<StripePlan>;
  status: stripeStatus;
  errorMessage: string;
  mostRecentPayment: StripePayment | null;
  clientSecret: string;
}

const initialState: StripeState = {
  plans: [],
  status: "unset",
  errorMessage: "",
  mostRecentPayment: null,
  clientSecret: ""

};

export const getPlans = createAsyncThunk(
  'api/stripe/get/plans',
  async () => {    
    return await axiosPublic.get('api/stripe',
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    ).then(
      (res) => {
        return res.data;
      }
    );
});

export const getMostRecentPayment = createAsyncThunk(
  'api/stripe/get/recent/payment',
  async () => {
    return await axiosPrivate.get('api/stripe/recent/payment',
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true 
      }
    ).then(res => res.data);
});

export const deleteMostRecentPayment = createAsyncThunk(
  'api/stripe/delete/recent/payment',
  async (_id: string) => {    
    return await axiosPrivate.delete('api/stripe/recent/payment/' + _id,
      {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
      }
    ).then(
      (res) => {
          return res.data;
      }
    );
});

export const createSubscription = createAsyncThunk(
  'api/stripe/create/subscription',
  async (params: { email: string, priceId: string }) => {    
      return await axiosPrivate.post('api/stripe/create/subscription',
          JSON.stringify(params),
          {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true
          }
      ).then(
          (res) => {
              return res.data;
          }
      );
});

export const changeSubscription = createAsyncThunk(
  'api/stripe/change/subscription',
  async (params: { priceId: string }) => {    
      return await axiosPrivate.post('api/stripe/change/subscription',
          JSON.stringify(params),
          {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true
          }
      ).then(
          (res) => {
              return res.data;
          }
      );
});

export const cancelSubscription = createAsyncThunk(
  'api/stripe/cancel/subscription',
  async (params: { subscriptionId: string}) => {    
      return await axiosPrivate.post('api/stripe/cancel/subscription',
          JSON.stringify(params),
          {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true
          }
      ).then(
          (res) => {
              return res.data;
          }
      );
});

export const setStripeStatus = createAsyncThunk(
  'set/stripe/status',
  async (status: stripeStatus) => {    
      return status;
});

export const StripeSlice = createSlice({
  name: 'strip',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(setStripeStatus.fulfilled, (state, action) => {
        state.status = action.payload;
      })
      .addCase(deleteMostRecentPayment.fulfilled, (state, action) => {
        state.status = 'idle';
        state.mostRecentPayment = action.payload;
      })
      .addCase(getMostRecentPayment.fulfilled, (state, action) => {
        state.status = 'idle';
        state.mostRecentPayment = action.payload;
      })
      .addCase(getPlans.fulfilled, (state, action) => {
        state.status = 'idle';
        state.plans = action.payload;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.status = 'idle';
        state.mostRecentPayment = action.payload.payment;
        state.clientSecret = action.payload.clientSecret;
      })
      .addCase(changeSubscription.fulfilled, (state, action) => {
        state.status = 'idle';
        state.mostRecentPayment = action.payload.payment;
        state.clientSecret = action.payload.clientSecret;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.status = 'subscriptionCanceled';
        state.mostRecentPayment = action.payload;
      })
  },
});

export const getStripe = (state: RootState) => state.stripe;
export default StripeSlice.reducer;
